import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { SITE_URL, DEFAULT_SEO, BRAND } from "../lib/site";

// Tags this component owns are marked so they can be replaced (and cleaned up)
// on navigation without touching the static tags in index.html.
const OWNED = "data-seo";

const upsertMeta = (attr, key, content) => {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const upsertLink = (rel, href) => {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

/**
 * Sets the document title, meta description, canonical URL, Open Graph /
 * Twitter tags and optional JSON-LD for the current route.
 */
export const Seo = ({ title, description, image, keywords, noindex = false, schema }) => {
  const { pathname } = useLocation();

  useEffect(() => {
    const fullTitle = title ? `${title} | ${BRAND.name}` : DEFAULT_SEO.title;
    const desc = description || DEFAULT_SEO.description;
    const url = `${SITE_URL}${pathname === "/" ? "" : pathname}`;
    const img = image || DEFAULT_SEO.image;

    document.title = fullTitle;
    upsertMeta("name", "description", desc);
    if (keywords) upsertMeta("name", "keywords", keywords);
    upsertMeta("name", "robots", noindex ? "noindex, nofollow" : "index, follow");
    upsertLink("canonical", url);

    upsertMeta("property", "og:title", fullTitle);
    upsertMeta("property", "og:description", desc);
    upsertMeta("property", "og:url", url);
    upsertMeta("property", "og:image", img);
    upsertMeta("name", "twitter:title", fullTitle);
    upsertMeta("name", "twitter:description", desc);
    upsertMeta("name", "twitter:image", img);

    document.head.querySelectorAll(`script[${OWNED}]`).forEach((el) => el.remove());
    if (schema) {
      const el = document.createElement("script");
      el.type = "application/ld+json";
      el.setAttribute(OWNED, "");
      el.textContent = JSON.stringify(schema);
      document.head.appendChild(el);
    }
  }, [title, description, image, keywords, noindex, schema, pathname]);

  return null;
};

export const breadcrumbSchema = (trail) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: trail.map(([name, path], i) => ({
    "@type": "ListItem",
    position: i + 1,
    name,
    item: `${SITE_URL}${path === "/" ? "" : path}`,
  })),
});
